import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from '../../utils/axios';
import Cart from './Cart';
import ItemList from './ItemList';
import CategoryList from './CategoryList';
import TabManager from './TabManager';
import CustomerRegistrationModal from './CustomerRegistrationModal';
import ReturnOrderModal from './ReturnOrderModal';
import PrintReceiptModal from './PrintReceiptModal';
import { toast } from 'react-hot-toast';
import Header from '../Header';
import { LanguageContext } from '../../context/LanguageContext';
import ReceiptPrinter from './payment/ReceiptPrinter';
import '../../App.css';

const POSSystem = () => {
    const receiptPrinterRef = useRef();
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [error, setError] = useState(null);
    const [loading] = useState(false);
    const [settings, setSettings] = useState(null);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [itemsRefreshKey, setItemsRefreshKey] = useState(0);
    const { language, t } = useContext(LanguageContext);

    // Tab management state
    const [tabs, setTabs] = useState([{ id: 1, cartItems: [] }]);
    const [activeTabId, setActiveTabId] = useState(1);

    // Get current tab's cart items
    const cartItems = tabs.find(tab => tab.id === activeTabId)?.cartItems || [];

    useEffect(() => {
        loadCategories();
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            // Try to get settings from API first
            const response = await axios.get('/settings');
            let settingsData = response.data;
            
            // Ensure printer settings exist
            if (!settingsData.printing) {
                settingsData.printing = {
                    printer_name: 'default',
                    printer_type: 'thermal',
                    paper_size: '80mm',
                    paper_height: 'auto',
                    font_size: '10',
                    copies: '1',
                    auto_print: true
                };
            }
            
            // Ensure restaurant info exists for receipt
            if (!settingsData.restaurant) {
                settingsData.restaurant = {
                    name: 'Khabeertech Restaurant',
                    name_ar: 'مطعم خبيرتك',
                    address: '123 Main Street',
                    address_ar: '١٢٣ الشارع الرئيسي',
                    phone: '123-456-7890',
                    tax_number: '123456789'
                };
            }
            
            // Save settings to localStorage for future use
            localStorage.setItem('posSettings', JSON.stringify(settingsData));
            setSettings(settingsData);
            
        } catch (err) {
            // If API call fails, try to get settings from localStorage
            const savedSettings = localStorage.getItem('posSettings');
            if (savedSettings) {
                setSettings(JSON.parse(savedSettings));
            } else {
                // Create default settings if nothing is available
                const defaultSettings = {
                    printing: {
                        printer_name: 'default',
                        printer_type: 'thermal',
                        paper_size: '80mm',
                        paper_height: 'auto',
                        font_size: '10',
                        copies: '1',
                        auto_print: true
                    },
                    restaurant: {
                        name: 'Khabeertech Restaurant',
                        name_ar: 'مطعم خبيرتك',
                        address: '123 Main Street',
                        address_ar: '١٢٣ الشارع الرئيسي',
                        phone: '123-456-7890',
                        tax_number: '123456789'
                    }
                };
                localStorage.setItem('posSettings', JSON.stringify(defaultSettings));
                setSettings(defaultSettings);
            }
            setError('Failed to load settings from API, using saved or default settings');
            console.error(err);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await axios.get('/categories');
            const data = response.data.data || [];
            setCategories(data);
            setSelectedCategory(null); // Set to null to show all items by default
        } catch (err) {
            setError('Failed to load categories');
            console.error(err);
        }
    };

    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
    };

    const addToCart = (item) => {
        const stock = item.available_stock ?? item.stock ?? 0;
        if (stock <= 0) {
            toast(language === 'ar' ? `تنبيه: مخزون "${item.name_ar}" نفذ` : `Warning: "${item.name}" is out of stock`, {
                icon: '\u26a0\ufe0f',
                duration: 3000
            });
        }
        setTabs(prevTabs => {
            return prevTabs.map(tab => {
                if (tab.id !== activeTabId) return tab;
                
                const existingItem = tab.cartItems.find(i => i.id === item.id);
                if (existingItem) {
                    return {
                        ...tab,
                        cartItems: tab.cartItems.map(i =>
                            i.id === item.id
                                ? {
                                    ...i,
                                    quantity: i.quantity + 1,
                                    price: i.customPrice || i.price
                                }
                                : i
                        )
                    };
                }
                return {
                    ...tab,
                    cartItems: [...tab.cartItems, {
                        ...item,
                        quantity: 1,
                        originalPrice: item.price,
                        price: item.price
                    }]
                };
            });
        });
    };

    const removeFromCart = (itemId) => {
        setTabs(prevTabs => {
            return prevTabs.map(tab => {
                if (tab.id !== activeTabId) return tab;

                const existingItem = tab.cartItems.find(i => i.id === itemId);
                if (existingItem && existingItem.quantity > 1) {
                    return {
                        ...tab,
                        cartItems: tab.cartItems.map(i =>
                            i.id === itemId
                                ? { ...i, quantity: i.quantity - 1 }
                                : i
                        )
                    };
                }
                return {
                    ...tab,
                    cartItems: tab.cartItems.filter(i => i.id !== itemId)
                };
            });
        });
    };

    const clearCart = () => {
        setTabs(prevTabs => {
            return prevTabs.map(tab => 
                tab.id === activeTabId 
                    ? { ...tab, cartItems: [] }
                    : tab
            );
        });
    };

    // Tab management functions
    const handleNewTab = () => {
        const newTabId = Math.max(...tabs.map(tab => tab.id)) + 1;
        setTabs(prevTabs => [...prevTabs, { id: newTabId, cartItems: [] }]);
        setActiveTabId(newTabId);
    };

    const handleCloseTab = (tabId) => {
        if (tabs.length === 1) return; // Don't close the last tab
        
        setTabs(prevTabs => prevTabs.filter(tab => tab.id !== tabId));
        if (activeTabId === tabId) {
            // Switch to the previous tab or the first available tab
            const remainingTabs = tabs.filter(tab => tab.id !== tabId);
            setActiveTabId(remainingTabs[0].id);
        }
    };

    const handleTabChange = (tabId) => {
        setActiveTabId(tabId);
    };

    const updateCartItem = (updatedItem) => {
        setTabs(prevTabs => {
            return prevTabs.map(tab => {
                if (tab.id !== activeTabId) return tab;
                
                return {
                    ...tab,
                    cartItems: tab.cartItems.map(item =>
                        item.id === updatedItem.id ? {
                            ...updatedItem,
                            // Mark price as custom if it's different from original
                            customPrice: updatedItem.price !== item.originalPrice ? updatedItem.price : undefined
                        } : item
                    )
                };
            });
        });
    };

    const handleCustomerRegistration = () => {
        setShowCustomerModal(true);
    };

    const handleCustomerRegistrationSuccess = (customer) => {
        setShowCustomerModal(false);
        toast.success(t('Customer registered successfully'));
    };

    // Print Receipt Function
    const handlePrintReceipt = async () => {
        try {
            // Show the dedicated print receipt modal
            setShowPrintModal(true);
        } catch (error) {
            console.error('Failed to open print modal:', error);
            toast.error(t('Failed to open print modal'));
        }
    };

    return (
        <div className={`app-container ${language === 'ar' ? 'rtl' : 'ltr'}`}>
            <Header 
                onRegisterCustomer={handleCustomerRegistration}
                onShowReturnModal={() => setShowReturnModal(true)}
                onPrintReceipt={handlePrintReceipt}
            />
            <CustomerRegistrationModal
                visible={showCustomerModal}
                onClose={() => setShowCustomerModal(false)}
                onSuccess={handleCustomerRegistrationSuccess}
            />
            <ReturnOrderModal
                isOpen={showReturnModal}
                onClose={() => setShowReturnModal(false)}
            />
            <PrintReceiptModal
                isOpen={showPrintModal}
                onClose={() => setShowPrintModal(false)}
            />
            <TabManager
                tabs={tabs}
                activeTab={activeTabId}
                onTabChange={handleTabChange}
                onNewTab={handleNewTab}
                onCloseTab={handleCloseTab}
            />
            <div className="main-content">
                <div className="menu-section">
                    {error && <div className="error-message">{error}</div>}
                    <div className="categories-wrapper">
                        <CategoryList
                            categories={categories}
                            selectedCategory={selectedCategory}
                            onSelectCategory={handleCategorySelect}
                        />
                    </div>
                    <div className="items-wrapper">
                        <ItemList
                            selectedCategory={selectedCategory}
                            onItemClick={addToCart}
                            refreshKey={itemsRefreshKey}
                        />
                    </div>
                </div>
                <div className="cart-section">
                    <Cart
                        items={cartItems}
                        onRemoveItem={removeFromCart}
                        onUpdateItem={updateCartItem}
                        onClearCart={clearCart}
                        onOrderCompleted={() => setItemsRefreshKey(prev => prev + 1)}
                        loading={loading}
                        receiptPrinterRef={receiptPrinterRef}
                    />
                    <ReceiptPrinter 
                        ref={receiptPrinterRef} 
                        settings={settings || {
                            printing: {
                                printer_name: 'default',
                                printer_type: 'thermal',
                                paper_size: '80mm',
                                paper_height: 'auto',
                                font_size: '10',
                                copies: '1',
                                auto_print: true
                            },
                            restaurant: {
                                name: 'Khabeertech Restaurant',
                                name_ar: 'مطعم خبيرتك',
                                address: '123 Main Street',
                                address_ar: '١٢٣ الشارع الرئيسي',
                                phone: '123-456-7890',
                                tax_number: '123456789'
                            }
                        }} 
                    />
                </div>
            </div>
        </div>
    );
};

export default POSSystem;
